import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Field, Form } from 'formik';
import { GUEST_RESERVE_SERVICE_STEP } from 'constants/reservation';
import AprosButton from 'components/aprosButton/index';
import InputSelectGuest from 'components/inputSelectGuest/index';
import InputTextGuest from 'components/inputTextGuest/index';
import InputTitleLabelGuest from 'components/inputTitleLabelGuest/index';
import StepperGuest from 'components/stepperGuest/index';
import Proctitle from 'components/proctitle/index';
import Linked from 'components/linked/index';
import axios from 'utils/axiosUtil';
import { CAR_LIST_SEARCH } from 'constants/apiURL';
import { carInfoValidationSchema } from 'utils/guestReserveValidationSchemaUtil';
import Path, { ERROR_MESSAGE_TRANSITION } from 'constants/routePath';
import { useDispatch } from 'react-redux';
import { messageModalActions } from 'slices/messageModalSlice';
import Title from '../../constants/title';
import { Helmet } from 'react-helmet';

const GuestCarInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [carList, setCarList] = useState([]);
  const [manufacturer, setManufacturer] = useState(
    location.state?.carInfo?.manufacturerCode || null
  );
  const [carModel, setCarModel] = useState(
    location.state?.carInfo
      ? `${location.state.carInfo.carModelCode}-${location.state.carInfo.carNameCode}`
      : null
  );

  useEffect(() => {
    if (!location.state?.shopAndDateInfo) {
      const message = ERROR_MESSAGE_TRANSITION;
      dispatch(messageModalActions.showMessageModal({ message }));
      navigate(Path.GUEST_RESERVATION);
      return;
    }
  }, [location.state, navigate]);

  // メーカー選択時に車種をリセット
  const handleManufacturerChange = (value, setFieldValue) => {
    setManufacturer(value);
    setCarModel(null);
    setFieldValue('carModel', '');
  };

  // 車両一覧取得APIを呼び出す
  const fetchCarList = async () => {
    const response = await axios.get(CAR_LIST_SEARCH);
    setCarList(response.body.carList || []);
  };

  // 初期レンダリング時に車両一覧を取得
  useEffect(() => {
    fetchCarList();
  }, []);

  // メーカーリスト
  const manufacturerOptions = carList.map(car => ({
    value: car.sryMkrCd,
    label: car.sryMkrNm1,
  }));

  // 車種リスト
  const carModelOptions = manufacturer
    ? carList
        .find(car => car.sryMkrCd === manufacturer)
        ?.modelList?.map(model => ({
          value: `${model.sshCd}-${model.kshCd}`,
          label: model.sshNm1,
        })) || []
    : [];

  return (
    <Formik
      initialValues={{
        manufacturer: location.state?.carInfo?.manufacturerCode || '',
        carModel: location.state?.carInfo
          ? `${location.state.carInfo.carModelCode}-${location.state.carInfo.carNameCode}`
          : '',
        year: location.state?.carInfo?.year || '',
        mileage: location.state?.carInfo?.mileage || '',
      }}
      validationSchema={carInfoValidationSchema}
      onSubmit={values => {
        const selectedManufacturerObj = carList.find(
          car => car.sryMkrCd === manufacturer
        );
        const selectedCarModelObj = selectedManufacturerObj?.modelList.find(
          model => `${model.sshCd}-${model.kshCd}` === carModel
        );
        navigate(Path.GUEST_USER_INFO, {
          state: {
            ...location.state,
            carInfo: {
              manufacturer: selectedManufacturerObj?.sryMkrNm1,
              manufacturerCode: selectedManufacturerObj?.sryMkrCd,
              carModel: selectedCarModelObj?.sshNm1,
              carModelCode: selectedCarModelObj?.sshCd,
              carNameCode: selectedCarModelObj?.kshCd,
              year: values.year,
              mileage: values.mileage,
            },
          },
        });
      }}
    >
      {({ values, handleChange, isValid, setFieldValue }) => {
        return (
          <Form>
            <div className="mt-4 lg:px-20">
              <Helmet>
                <title>{Title.GUEST_RESERVATION}</title>
              </Helmet>
              <StepperGuest
                labels={GUEST_RESERVE_SERVICE_STEP}
                previousStep={location.state?.previousStep}
                activeStep={1}
                fullWidth={true}
                className={{
                  stepperClass: 'max-sm:mx-8 sm:mx-36',
                  bottomClass: 'mx-0 h-full w-full text-left',
                }}
                stepPaths={[
                  Path.GUEST_SHOP_AND_DATE_SELECT,
                ]}
                navigate={navigate}
                locationState={location.state}
              />
              <div className="mt-12 sm:mt-24">
                <Proctitle
                  useH1={true}
                  text="STEP2 お車情報の入力"
                  className={{ textClass: 'whitespace-nowrap' }}
                />
              </div>
              <div className="mt-6 sm:mt-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="sm:mt-12 sm:w-1/3">
                    <InputTitleLabelGuest title="メーカー" required={true} />
                  </div>
                  <div className="flex flex-row sm:mt-12 sm:w-full">
                    <InputSelectGuest
                      fieldName="manufacturer"
                      placeholder="選択してください"
                      options={manufacturerOptions}
                      value={values.manufacturer || null}
                      onChange={value => {
                        handleManufacturerChange(value, setFieldValue);
                        handleChange({
                          target: { name: 'manufacturer', value },
                        });
                      }}
                      disabled={carList.length === 0}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6 sm:mt-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="sm:mt-12 sm:w-1/3">
                    <InputTitleLabelGuest title="車種" required={true} />
                  </div>
                  <div className="flex flex-row sm:mt-12 sm:w-full">
                    <InputSelectGuest
                      fieldName="carModel"
                      placeholder="選択してください"
                      options={carModelOptions}
                      value={values.carModel || null}
                      onChange={value => {
                        setCarModel(value);
                        handleChange({ target: { name: 'carModel', value } });
                      }}
                      disabled={!values.manufacturer}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6 sm:mt-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="sm:mt-12 sm:w-1/4">
                    <div className="flex items-center">
                      <InputTitleLabelGuest title="年式" required={false} />
                      <span className="text-xs mt-1 break-words text-[#4C657C] sm:min-w-[40px]">
                        西暦・半角数字
                      </span>
                    </div>
                  </div>
                  <div className="flex w-[200px] flex-row items-center gap-2 sm:w-1/4">
                    <Field name="year">
                      {({ field, form }) => {
                        const hasError = (form.errors[field.name] && form.touched[field.name]);
                        return (
                          <InputTextGuest
                            formik={form}
                            fieldName={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="2020"
                            error={hasError ? form.errors[field.name] : ''}
                            touched={form.touched[field.name]}
                            suffix="年"
                          />
                        );
                      }}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="mt-6 sm:mt-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="sm:mt-12 sm:w-1/4">
                    <div className="flex items-center">
                      <InputTitleLabelGuest title="走行距離" required={false} />
                      <span className="text-xs mt-1 break-words text-[#4C657C] sm:min-w-[40px]">
                        半角数字
                      </span>
                    </div>
                  </div>
                  <div className="flex w-[200px] flex-row items-center gap-2 sm:w-1/4">
                    <Field name="mileage">
                      {({ field, form }) => {
                        const hasError = (form.errors[field.name] && form.touched[field.name]);
                        return (
                          <InputTextGuest
                            formik={form}
                            fieldName={field.name}
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            placeholder="50000"
                            error={hasError ? form.errors[field.name] : ''}
                            touched={form.touched[field.name]}
                            suffix="km"
                          />
                        );
                      }}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="mt-8 text-center sm:mt-12">
                <AprosButton
                  text="お客様情報の入力へ進む"
                  comClassName="sub"
                  type="submit"
                  className="mb-4 w-[260px] hover:text-navy"
                  disabled={!isValid || !(manufacturer && carModel)}
                />
              </div>
              <div className="mb-12 mt-8 flex items-center justify-center text-center sm:mb-24">
                <Linked
                  text="店舗・日時選択へ戻る"
                  onClick={() => navigate(Path.GUEST_SHOP_AND_DATE_SELECT, { state: location.state })}
                  isLine
                  linkIcon="L"
                  className="font-bold"
                />
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default GuestCarInfo;
